import { getServiceUrl } from '../api.config';
import { Application } from '../types';
import { ApiClientOptions } from '../types/api';
import ApiClient from '../utils/ApiClient';
import qs from '../utils/query';
import {
  isBoolean,
  isNotEmptyArray,
  isNotEmptyString,
  isPositiveInteger,
} from '../utils/validators';

export default class ApplicationApi extends ApiClient {
  public constructor(options?: ApiClientOptions) {
    super(getServiceUrl('application'), options);
  }
  // ===========================================================================
  //                              APPLICATIONS APIs
  // ===========================================================================

  public ApplicationList(
    {
      idIn,
      isActive,
      isEnabled,
      organizationId,
      typeIn,
      useCache = true,
      visibilities,
    }: {
      idIn?: number[];
      isActive?: boolean;
      isEnabled?: boolean;
      organizationId?: number;
      typeIn?: number[];
      useCache?: boolean;
      visibilities?: boolean;
    } = { useCache: true },
  ) {
    const params: {
      filter_id_in?: number[];
      filter_type_in?: number[];
      filter_visibilities?: boolean;
      is_active?: boolean;
      is_enabled?: boolean;
      organization_id?: number;
    } = {};
    if (isBoolean(isEnabled)) {
      params.is_enabled = isEnabled;
    }
    if (isBoolean(isActive)) {
      params.is_active = isActive;
    }
    if (isBoolean(visibilities)) {
      params.filter_visibilities = visibilities;
    }
    if (isNotEmptyArray(idIn)) {
      params.filter_id_in = idIn;
    }
    if (isNotEmptyArray(typeIn)) {
      params.filter_type_in = typeIn;
    }
    if (organizationId) {
      params.organization_id = organizationId;
    }
    const query = '/application/v1/applications';
    return this.get<Application[]>(query, qs.stringify(params, true), useCache);
  }

  public ApplicationCreate(data: any) {
    if (!data) {
      const error = {
        message: 'Missing data',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = '/application/v1/applications';
    return this.post(query, data);
  }

  public ApplicationUpdate(data: any) {
    if (!data) {
      const error = {
        message: 'Missing data',
        name: 'bad parameter',
      };
      throw error;
    }
    if (!data.id) {
      const error = {
        message: 'Missing parameter: id',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/application/v1/applications/${data.id}`;
    return this.post(query, data);
  }

  public ApplicationEnable(applicationId: number, organizationId: number) {
    if (!applicationId) {
      const error = {
        message: 'Missing parameter: applicationid',
        name: 'bad parameter',
      };
      throw error;
    }
    if (!organizationId) {
      const error = {
        message: 'Missing parameter: organizationId',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/application/v1/applications/${applicationId}/enable?organization_id=${organizationId}`;
    return this.post(query);
  }

  public ApplicationDisable(applicationId: number, organizationId: number) {
    if (!applicationId) {
      const error = {
        message: 'Missing parameter: applicationid',
        name: 'bad parameter',
      };
      throw error;
    }
    if (!organizationId) {
      const error = {
        message: 'Missing parameter: organizationId',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/application/v1/applications/${applicationId}/disable?organization_id=${organizationId}`;
    return this.post(query);
  }

  // TODO Remove when we remove the old page
  public OrganizationApplication(application: any) {
    if (!application) {
      const error = {
        message: 'Missing parameter : application',
        name: 'bad parameter',
      };
      throw error;
    }
    const action = application.active ? 'activate' : 'deactivate';
    const query = `/application/v1/applications/${application.id}/${action}`;
    return this.post(query);
  }

  public ApplicationHasOrganizationEnabled() {
    const query = '/application/v1/applications/organizations';
    return this.get(query);
  }

  public ApplicationCategories() {
    const query = '/application/v1/applications/categories';
    return this.get(query);
  }

  public ApplicationDeactivate(applicationId: number) {
    if (!isPositiveInteger(applicationId)) {
      const error = {
        message: 'Missing parameter : applicationId',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/application/v1/applications/${applicationId}/deactivate`;
    return this.post(query);
  }

  public ApplicationActivate(applicationId: number) {
    if (!isPositiveInteger(applicationId)) {
      const error = {
        message: 'Missing parameter : applicationId',
        name: 'bad parameter',
      };
      throw error;
    }
    const query = `/application/v1/applications/${applicationId}/activate`;
    return this.post(query);
  }

  public ApplicationNielsen(queries: any = {}) {
    const query = '/application/v1/nielsen';
    const params: any = {};
    if (isNotEmptyString(queries.market)) {
      params.market = queries.market;
    }
    if (isNotEmptyString(queries.period)) {
      params.period = queries.period;
    }
    return this.get(query, qs.stringify(params, true));
  }

  public ApplicationNielsenV2(queries: any = {}) {
    const query = '/application/v2/nielsen';
    const params: any = {};
    if (isNotEmptyString(queries.market)) {
      params.market = queries.market;
    }
    if (isNotEmptyString(queries.period)) {
      params.period = queries.period;
    }
    if (isNotEmptyString(queries.searchName)) {
      params.search_name = queries.searchName;
    }
    if (isNotEmptyString(queries.searchGTIN)) {
      params.search_gtin = queries.searchGTIN;
    }
    return this.get(query, qs.stringify(params, true));
  }

  public ApplicationNielsenDownloadExport() {
    const query = '/application/v1/nielsen/export';
    return this.getBlob(query);
  }

  public ApplicationNielsenPeriods() {
    const query = '/application/v1/nielsen/periods';
    return this.get(query);
  }

  public SettingApplications(settingKey: string) {
    const query = `/application/v1/applications/setting/${settingKey}`;
    return this.get(query);
  }
}
